import React from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import Box from '@mui/material/Box';
import 'dayjs/locale/en-gb';
import FormHelperText from '@mui/material/FormHelperText';
import { TimeField, TimeFieldProps } from '@mui/x-date-pickers/TimeField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { select12HourFormat } from 'redux/selectors/selectors';

export type TControlledInputProps<T extends FieldValues> = TimeFieldProps<T> &
  UseControllerProps<T> & {
    errorMessage: string;
  };

export const TimeFieldWrapper = <T extends FieldValues>({
  name,
  control,
  errorMessage,
  ...props
}: TControlledInputProps<T>) => {
  const { field } = useController<T>({ control, name });
  const is12HourFormat = useAppSelector(select12HourFormat);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={is12HourFormat ? 'en' : 'en-gb'}
    >
      <Box>
        <TimeField
          {...field}
          {...props}
          slotProps={{
            textField: {
              variant: 'filled',
              sx: {
                width: '100%',
              },
              inputProps: {
                sx: {
                  padding: '9px 0 9px 12px',
                },
              },
              InputProps: {
                disableUnderline: true,
                sx: {
                  borderRadius: { lg: '8px', md: '6px', sm: '6px', xs: '6px' },
                  minHeight: { lg: '44px', md: '46px', sm: '46px', xs: '46px' },
                  border: '1px solid',
                  borderColor: errorMessage.length > 1 ? 'error.main' : 'transparent',
                },
              },
            },
          }}
        />
        <FormHelperText>{errorMessage}</FormHelperText>
      </Box>
    </LocalizationProvider>
  );
};
